// @djsem/ui — AI Video Generation Studio UX model (Prompt 22B).
//
// Pure, framework-free logic backing the video-generation screens: output-type/preset options,
// source eligibility gating (an audio-reactive render needs a local/licensed audio file — no
// platform-metadata-only sources), active rights/credit confirmation (no pre-ticked checkbox),
// and job status → UI copy. Mirrors stemStudioModel.ts's shape for the video domain. React
// renders this; the rules stay unit-testable without a DOM.

/** A video generation output type offered to the user. */
export type DJSEMAIVideoOutputTypeUI =
  | "social_teaser" | "vertical_social_clip" | "spotify_canvas_loop" | "youtube_visualizer_clip"
  | "full_track_visualizer" | "release_announcement" | "campaign_motion_loop"
  | "projection_loop" | "led_wall_loop" | "show_pack_preview";

/** A selectable video output-type option with its aspect ratio and best-use description. */
export interface VideoOutputTypeOption {
  outputType: DJSEMAIVideoOutputTypeUI;
  label: string;
  aspectRatio: string;
  bestFor: string;
}

/** Required output-type presets (prompt 22B §6/§10). */
export const VIDEO_OUTPUT_TYPE_OPTIONS: readonly VideoOutputTypeOption[] = [
  { outputType: "social_teaser", label: "Social teaser", aspectRatio: "9:16", bestFor: "Short vertical teaser for a release or set moment" },
  { outputType: "vertical_social_clip", label: "Vertical social clip", aspectRatio: "9:16", bestFor: "Instagram/TikTok clip" },
  { outputType: "spotify_canvas_loop", label: "Spotify Canvas-style loop", aspectRatio: "9:16", bestFor: "Short seamless loop for streaming platforms" },
  { outputType: "youtube_visualizer_clip", label: "YouTube visualizer clip", aspectRatio: "16:9", bestFor: "Longer-form visualizer for YouTube" },
  { outputType: "full_track_visualizer", label: "Full-track visualizer", aspectRatio: "16:9", bestFor: "Whole-track audio-reactive visualizer" },
  { outputType: "release_announcement", label: "Release announcement", aspectRatio: "1:1", bestFor: "Announcement post for a new release" },
  { outputType: "campaign_motion_loop", label: "Campaign motion loop", aspectRatio: "1:1", bestFor: "Looping motion asset for a campaign pack" },
  { outputType: "projection_loop", label: "Projection loop", aspectRatio: "16:9", bestFor: "Loop for a projection show element" },
  { outputType: "led_wall_loop", label: "LED wall loop", aspectRatio: "16:9", bestFor: "Loop for an LED wall show element" },
  { outputType: "show_pack_preview", label: "Show pack preview", aspectRatio: "16:9", bestFor: "Preview clip attached to a show pack" },
] as const;

/**
 * Look up a video output type's option details.
 * @param outputType - The output type to describe.
 * @returns The matching {@link VideoOutputTypeOption}, or undefined if unknown.
 */
export function describeVideoOutputType(outputType: DJSEMAIVideoOutputTypeUI): VideoOutputTypeOption | undefined {
  return VIDEO_OUTPUT_TYPE_OPTIONS.find((o) => o.outputType === outputType);
}

// ---- source eligibility ------------------------------------------------------
// The real renderer (apps/ai-video-renderer) needs a local/licensed audio file — it cannot
// render from a streamed platform reference (no protected-audio download/resale, per the
// product boundary). Mirrors checkStemSourceEligibility's shape/reasoning.

/** Whether/why a source may (or may not) be used for video generation. */
export type VideoSourceEligibility = "eligible" | "missing_audio" | "platform_metadata_only" | "rights_blocked";

/** Inputs needed to check a source's video-generation eligibility. */
export interface VideoSourceCheckInput {
  hasLocalAudioFile: boolean;
  sourcePlatform?: "spotify" | "soundcloud" | "youtube" | "local" | "upload";
  rightsState: "unknown" | "owned" | "licensed" | "restricted" | "ai_generated_uncleared";
}

/** Result of a video-generation source eligibility check. */
export interface VideoSourceCheckResult {
  eligibility: VideoSourceEligibility;
  reason: string;
}

/**
 * Check whether a source may be used for video generation (needs a local/licensed audio file).
 * @param input - The source's file/platform/rights state.
 * @returns The eligibility verdict and a human-readable reason.
 */
export function checkVideoSourceEligibility(input: VideoSourceCheckInput): VideoSourceCheckResult {
  if (input.rightsState === "restricted") {
    return { eligibility: "rights_blocked", reason: "This source is marked restricted — video generation is blocked until rights are cleared." };
  }
  const isStreamedPlatform = input.sourcePlatform === "spotify" || input.sourcePlatform === "soundcloud" || input.sourcePlatform === "youtube";
  if (isStreamedPlatform && !input.hasLocalAudioFile) {
    return {
      eligibility: "platform_metadata_only",
      reason: `${input.sourcePlatform} references are metadata-only — DJSEM cannot render video from streamed audio. Supply a local/licensed file to enable video generation.`,
    };
  }
  if (!input.hasLocalAudioFile) {
    return { eligibility: "missing_audio", reason: "No local audio file found for this track — locate or re-import the file to generate a video." };
  }
  return { eligibility: "eligible", reason: "Ready — this source can be rendered." };
}

// ---- rights/credit confirmation ----------------------------------------------

/** Active (never pre-ticked) rights + credit confirmation checkboxes for starting a video job. */
export interface VideoRightsConfirmationState {
  rightsChecked: boolean;
  creditChecked: boolean;
}

/**
 * No job may be created until BOTH boxes are actively checked — no pre-ticked checkbox.
 * @param state - Current confirmation checkbox state.
 * @returns True only if both boxes are checked.
 */
export function canCreateVideoJob(state: VideoRightsConfirmationState): boolean {
  return state.rightsChecked && state.creditChecked;
}

/** Canonical rights/credit confirmation copy shown before creating a video generation job. */
export const VIDEO_RIGHTS_CONFIRMATION_COPY = [
  "AI-generated video may use project context, brand assets, source artwork, and track information.",
  "Only use references and source material you own, control, or are licensed to use.",
  "DJSEM does not grant legal clearance for generated video.",
  "Generated videos are recorded as rights-uncleared until you explicitly clear them.",
] as const;

// ---- job status → UI copy -----------------------------------------------------

/** Lifecycle status of a video generation job. */
export type VideoJobStatusUI =
  | "created" | "awaiting_rights_confirmation" | "awaiting_credits"
  | "queued" | "running" | "completed" | "failed" | "cancelled";

/**
 * Color tone for a video job status badge.
 * @param status - The job's current status.
 * @returns The tone to render it with.
 */
export function videoJobStatusTone(status: VideoJobStatusUI): "neutral" | "ok" | "warn" | "danger" {
  switch (status) {
    case "completed": return "ok";
    case "failed": return "danger";
    case "awaiting_rights_confirmation": case "awaiting_credits": case "cancelled": return "warn";
    default: return "neutral";
  }
}

/**
 * Human-readable label for a video job status.
 * @param status - The job's current status.
 * @returns The display label.
 */
export function videoJobStatusLabel(status: VideoJobStatusUI): string {
  switch (status) {
    case "created": return "Created";
    case "awaiting_rights_confirmation": return "Awaiting rights confirmation";
    case "awaiting_credits": return "Awaiting credit confirmation";
    case "queued": return "Queued — real rendering runs locally via bin/djsem video render";
    case "running": return "Rendering";
    case "completed": return "Review required";
    case "failed": return "Failed";
    case "cancelled": return "Cancelled";
    default: return status;
  }
}

// ---- per-output review state --------------------------------------------------

/** Per-output review state for a generated video. */
export type VideoOutputReviewStateUI = "not_reviewed" | "needs_review" | "approved" | "rejected";

/**
 * Color tone for a video output review-state badge.
 * @param state - The output's current review state.
 * @returns The tone to render it with.
 */
export function videoOutputReviewTone(state: VideoOutputReviewStateUI): "neutral" | "ok" | "warn" | "danger" {
  switch (state) {
    case "approved": return "ok";
    case "rejected": return "danger";
    case "needs_review": return "warn";
    default: return "neutral";
  }
}

/**
 * Human-readable label for a video output review state.
 * @param state - The output's current review state.
 * @returns The display label.
 */
export function videoOutputReviewLabel(state: VideoOutputReviewStateUI): string {
  switch (state) {
    case "not_reviewed": return "Not reviewed";
    case "needs_review": return "Needs review";
    case "approved": return "Approved";
    case "rejected": return "Rejected";
    default: return state;
  }
}

/**
 * Actions offered per review state — approve/reject are the only user-settable states the
 * worker route accepts (needs_review is also settable to explicitly flag for a second pass).
 * @param state - The output's current review state.
 * @returns The offered actions for this state.
 */
export function videoOutputActionsFor(state: VideoOutputReviewStateUI): Array<"approve" | "reject" | "needs_review"> {
  switch (state) {
    case "not_reviewed": case "needs_review": return ["approve", "reject"];
    case "rejected": return ["needs_review"];
    case "approved": return ["reject"];
    default: return [];
  }
}
